import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { RevealOnScroll, FadeUp } from "../../lib/motion";
import { useLang } from "../../i18n/LanguageContext";

const ServiceRow = ({ n, title, text, details, open, onToggle }) => {
  return (
    <div
      className={`border-t border-black transition-colors duration-300 ${
        open ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-50"
      }`}
      data-testid={`service-${n}`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`service-toggle-${n}`}
        className="w-full text-left grid grid-cols-12 gap-4 items-center py-8 md:py-10 px-1"
      >
        <div className="col-span-2 md:col-span-1">
          <span className="font-display text-sm md:text-base font-bold">{n}</span>
        </div>
        <div className="col-span-8 md:col-span-5">
          <h3 className="font-display font-bold uppercase text-2xl md:text-4xl tracking-tight">
            {title}
          </h3>
        </div>
        <div className="hidden md:block md:col-span-5">
          <p
            className={`text-base leading-relaxed max-w-md transition-opacity duration-300 ${
              open ? "opacity-90" : "opacity-60"
            }`}
          >
            {text}
          </p>
        </div>
        <div className="col-span-2 md:col-span-1 flex justify-end">
          <Plus
            size={28}
            className={`transition-transform duration-300 ${open ? "rotate-45" : ""}`}
          />
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-12 gap-4 px-1 pb-10 md:pb-12">
              <div className="col-span-12 md:col-start-2 md:col-span-10">
                <p className="md:hidden text-white/80 text-base leading-relaxed mb-6">
                  {text}
                </p>
                <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4 max-w-3xl">
                  {details.map((d, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="mt-2 h-1.5 w-1.5 bg-white shrink-0" />
                      <span className="text-base md:text-lg leading-relaxed">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Services = () => {
  const { t } = useLang();
  const s = t.services;
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="services" className="bg-white" data-testid="services-section">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-24 md:py-40">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
          <div>
            <FadeUp>
              <span className="text-sm md:text-base uppercase tracking-[0.25em] font-bold text-neutral-500">
                {s.label}
              </span>
            </FadeUp>
            <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.9] tracking-tight mt-6">
              {s.titleLines.map((line, i) => (
                <RevealOnScroll key={i} delay={i * 0.1}>
                  {line}
                </RevealOnScroll>
              ))}
            </h2>
          </div>
          <FadeUp delay={0.2} className="max-w-sm">
            <p className="text-neutral-700 text-lg leading-relaxed">{s.subtitle}</p>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400">
              {s.hint}
            </p>
          </FadeUp>
        </div>

        <div className="border-b border-black">
          {s.items.map((item, i) => (
            <ServiceRow
              key={i}
              n={String(i + 1).padStart(2, "0")}
              title={item.title}
              text={item.text}
              details={item.details}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
